import CardWithTitle from 'components/atoms/CardWithTitle'
import React, {useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import PurchaseHistoryListOrganizationStyle from 'pages/ProductHistoryOrganization/ProductHistoryOrganizationDetail/style'
import {
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import useProductManagement from 'features/productManagement/useProductManagement'
import {
  PAYMENT_METHOD,
  PREPAYMENT_ITEM,
  TYPE_GOOD_TYPE,
  TYPE_INDICATOR_PRODUCT,
  UNIT_PRICES,
} from 'pages/ProductsPages/constant'
import {isoStringToDateAndTimeString} from 'helpers/dateHelper'
import {useParams} from 'react-router-dom'
import {fetchProductDetailApi, ProductAdminDetailResponse} from 'api/productApi'
import {navigate} from 'AppHistory'
import RouteConstant from 'constants/RouteConstant'
import {formatPriceNumber} from 'helpers/uiHelper'
import {usdToCent} from 'helpers/paymentHelper'

const useStyles = makeStyles(PurchaseHistoryListOrganizationStyle)

interface Params {
  id: string
}

const ProductHistoryOrganizationDetail = () => {
  const {t, i18n} = useTranslation()
  const classes = useStyles()
  const {id} = useParams<Params>()
  const {
    listPackage,
    getPackageList,
    listProductPurchaseHistoryOrganization,
    getQuantityLabel,
    getPeriodProduct,
    getOrder,
  } = useProductManagement()
  const [detailProduct, setDetailProduct] =
    React.useState<ProductAdminDetailResponse>()

  const listHeader = [
    {
      id: 0,
      label: t('IDivision'),
    },
    {
      id: 1,
      label: t('IAnalysisIndicatorsV1'),
    },
    {
      id: 2,
      label: t('IPaidPoints'),
    },
  ]

  const getCLS = (id: string) => {
    if (!listPackage) return ''
    const item = listPackage.filter((e) => e.itemId === id)
    if (item.length) {
      return item[0].cls
    }
    return ''
  }

  const getPackageName = (id: string) => {
    if (!listPackage) return ''
    const item = listPackage.filter((e) => e.itemId === id)
    return (
      item[0]?.displayInfos?.find(
        (item) => item.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
      )?.name || ''
    )
  }

  const data = useMemo(() => {
    const value = detailProduct?.offer?.offerItems
      .map((item) => {
        return {
          ...item,
          cls: getCLS(item.pkgItemId),
          pkgName: getPackageName(item.pkgItemId),
          order: getOrder(item.pkgItemId),
        }
      })
      .toSorted((a, b) => a.cls.localeCompare(b.cls) || a.order - b.order)

    return value
  }, [detailProduct, i18n.language])

  const rowSpanMap = (data ?? []).reduce<Record<string, number>>(
    (acc, curr) => {
      acc[curr.cls] = (acc[curr.cls] || 0) + 1
      return acc
    },
    {},
  )
  const seenCls = new Set<string>()

  const currentOrder = listProductPurchaseHistoryOrganization?.content.find(
    (order) => order.order.orderId === id,
  )

  const isChargedProduct =
    currentOrder?.order.orderItems[0].goodsType === TYPE_GOOD_TYPE.CHARGE

  const getItemQuantity = useMemo(() => {
    return (item: any) => {
      if (isChargedProduct) {
        const currentItem = currentOrder?.order.orderItems.find(
          (order) => order.chargeId === item.id,
        )
        return currentItem?.purchaseCount === 0
          ? '-'
          : `${currentItem?.purchaseCount || 0} ${
              item.cls === 'PBM' ? t('IMinute') : t('ICase')
            }`
      }
      return getQuantityLabel(item.offerAmount) !== t('IUnlimited')
        ? `${getQuantityLabel(item.offerAmount)} ${
            item.cls === 'PBM' ? t('IMinutes') : t('ICase')
          }`
        : t('IUnlimited')
    }
  }, [t, currentOrder, isChargedProduct])

  const productName = detailProduct?.goods?.displayInfos?.find((info) =>
    i18n.language === 'ko' ? info.langCode === 'ko' : info.langCode === 'en',
  )?.name

  const productDescription = detailProduct?.goods?.displayInfos?.find((info) =>
    i18n.language === 'ko' ? info.langCode === 'ko' : info.langCode === 'en',
  )?.description

  const getProductType = () => {
    if (detailProduct?.sale.paymentMethod === PAYMENT_METHOD.PREPAYMENT) {
      if (detailProduct.offer.offerType === PREPAYMENT_ITEM.PROVIDER) {
        return t('IUpfrontPlanMethod')
      }
      if (detailProduct.offer.offerType === PREPAYMENT_ITEM.MONTHLY_OFFER) {
        return t('IMonthlyPlanMethod')
      }
    }
    return t('IManual')
  }

  const productTypeLabel = getProductType()

  useEffect(() => {
    if (!listProductPurchaseHistoryOrganization?.content?.length) {
      navigate(RouteConstant.PRODUCTS_PURCHASE_HISTORY_ORGANIZATION.path)
    }
    if (!listPackage?.length) {
      getPackageList({
        target: TYPE_INDICATOR_PRODUCT.ALL,
      })
    }
  }, [listPackage, listProductPurchaseHistoryOrganization])

  useEffect(() => {
    const fetchGoodsName = async () => {
      try {
        const res = await fetchProductDetailApi({
          goodsId: currentOrder?.order.orderItems[0].goodsId || '',
        })
        setDetailProduct(res.data)
      } catch (error) {
        console.error(error)
      }
    }

    if (currentOrder?.order.orderItems[0].goodsId) {
      fetchGoodsName()
    }
  }, [currentOrder])

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: 4,
      }}
    >
      <CardWithTitle
        title={t('IProductPurchaseDetailOrganization')}
        style={{
          border: 'none',
        }}
      >
        <div className={classes.detailTitle}>{t('IPurchaseDetail')}</div>
        <div className={classes.cardWrapper}>
          <div
            className={classes.informationWrapper}
            style={{
              gap: 20,
            }}
          >
            <div className={classes.cardTitle}>
              ■ {t('IProductInformation')}
            </div>
            <div className={`${classes.card}`}>
              <div className={classes.cardTitle}>{productName}</div>

              <div className={classes.productSection}>
                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IAmount')}:{' '}
                    {currentOrder?.order.price.currency || 'KRW'}{' '}
                    {formatPriceNumber(
                      currentOrder?.order.price.currency === UNIT_PRICES.USD
                        ? usdToCent(
                            Number(currentOrder?.order.price.price || 0),
                          )
                        : Number(currentOrder?.order.price.price || 0),
                    )}
                  </span>
                </div>

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IType')}: {productTypeLabel}
                  </span>
                </div>

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IPeriod')}:{' '}
                    {detailProduct ? getPeriodProduct(detailProduct.offer) : ''}
                  </span>
                </div>

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IDescription')}: {productDescription}
                  </span>
                </div>
              </div>
              <Table
                size='small'
                className={classes.table}
                style={{tableLayout: 'fixed'}}
              >
                <TableHead className={classes.tableHeader}>
                  {listHeader.map((item) => (
                    <TableCell
                      align='center'
                      padding='checkbox'
                      key={item.id}
                      style={{
                        whiteSpace: 'pre',
                      }}
                      className={classes.headerMax}
                    >
                      {t(item.label)}
                    </TableCell>
                  ))}
                </TableHead>
                <TableBody>
                  {data?.map((item) => {
                    const isFirstOccurCls = !seenCls.has(item.cls)
                    if (isFirstOccurCls) seenCls.add(item.cls)
                    return (
                      <TableRow key={item.pkgItemId}>
                        {isFirstOccurCls && (
                          <TableCell
                            align='center'
                            padding='checkbox'
                            rowSpan={rowSpanMap[item.cls]}
                            className={classes.headerMax}
                          >
                            {item.cls}
                          </TableCell>
                        )}
                        <TableCell
                          align='center'
                          padding='checkbox'
                          className={classes.headerMax}
                        >
                          {item.pkgName}
                        </TableCell>
                        <TableCell
                          align='center'
                          padding='checkbox'
                          className={classes.headerMax}
                        >
                          {getItemQuantity(item)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
          <div className={classes.informationWrapper}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div className={classes.cardTitle}>
                ■ {t('IPaymentInformation')}
              </div>
              {currentOrder?.payment.receiptUrl && (
                <Button
                  color='primary'
                  variant='contained'
                  onClick={() => {
                    window.open(`${currentOrder?.payment.receiptUrl}`, '_blank')
                  }}
                >
                  {t('IReceipt')}
                </Button>
              )}
            </div>
            <div className={`${classes.card}`}>
              <div className={classes.productSection}>
                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IOrderNumber')}: {currentOrder?.payment.paymentNo}
                  </span>
                </div>

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IPurchaseAmount')}:{' '}
                    {currentOrder?.order.price.currency || 'KRW'}{' '}
                    {formatPriceNumber(
                      currentOrder?.order.price.currency === UNIT_PRICES.USD
                        ? usdToCent(
                            Number(currentOrder?.order.price.price || 0),
                          )
                        : Number(currentOrder?.order.price.price || 0),
                    )}{' '}
                  </span>
                </div>

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IPaidDate')}:{' '}
                    {isoStringToDateAndTimeString(
                      String(currentOrder?.order.orderAt || ''),
                    )}
                  </span>
                </div>

                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IPaymentMethod')}:{' '}
                    {`${currentOrder?.payment.paymentType} / ${productTypeLabel}`}
                  </span>
                </div>
                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IFirstName')}: {currentOrder?.payment.customer.name}
                  </span>
                </div>
                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IPhone')}: {currentOrder?.payment.customer.phoneNumber}
                  </span>
                </div>
                <div className={classes.infoItem}>
                  <span className={classes.bullet}>•</span>
                  <span className={classes.infoText}>
                    {t('IEmail')}: {currentOrder?.payment.customer.email}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {currentOrder && currentOrder?.cancellations.length > 0 && (
            <div className={classes.informationWrapper}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div className={classes.cardTitle}>
                  ■ {t('IPaymentCancellationInformation')}
                </div>
                {currentOrder?.cancellations[0].receiptUrl && (
                  <Button
                    color='primary'
                    variant='contained'
                    onClick={() => {
                      window.open(
                        `${currentOrder?.cancellations[0].receiptUrl}`,
                        '_blank',
                      )
                    }}
                  >
                    {t('IReceipt')}
                  </Button>
                )}
              </div>
              <div className={`${classes.card}`}>
                <div className={classes.productSection}>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IOrderNumber')}: {currentOrder?.payment.paymentNo}
                    </span>
                  </div>

                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IPurchaseAmount')}:{' '}
                      {currentOrder?.cancellations[0].price.currency || 'KRW'}{' '}
                      {formatPriceNumber(
                        currentOrder?.cancellations[0].price.currency ===
                          UNIT_PRICES.USD
                          ? usdToCent(
                              Number(
                                currentOrder?.cancellations[0].price.price || 0,
                              ),
                            )
                          : Number(
                              currentOrder?.cancellations[0].price.price || 0,
                            ),
                      )}
                    </span>
                  </div>

                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IPaidDate')}:{' '}
                      {isoStringToDateAndTimeString(
                        String(
                          new Date(currentOrder.cancellations[0].canceledAt),
                        ),
                      )}
                    </span>
                  </div>

                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IPaymentMethod')}:{' '}
                      {`${currentOrder.cancellations[0].paymentType} / ${productTypeLabel}`}
                    </span>
                  </div>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IFirstName')}: {currentOrder?.payment.customer.name}
                    </span>
                  </div>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IPhone')}:{' '}
                      {currentOrder?.payment.customer.phoneNumber}
                    </span>
                  </div>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('IEmail')}: {currentOrder?.payment.customer.email}
                    </span>
                  </div>
                  <div className={classes.infoItem}>
                    <span className={classes.bullet}>•</span>
                    <span className={classes.infoText}>
                      {t('ICancelReason')}:{' '}
                      {currentOrder?.cancellations[0].reason}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardWithTitle>
    </div>
  )
}

export default ProductHistoryOrganizationDetail
